import { useTranslation } from 'react-i18next';
import { CURRENCIES } from '@/lib/calculationEngine';
import { formatAssumptionValue } from '@/lib/assumptions';
import { ProvenanceBadge } from '@/components/trust/ProvenanceBadge';

const symbolOf = (config) => CURRENCIES.find(c => c.value === config?.currency)?.symbol || '€';
const money = (config) => (value) => `${symbolOf(config)}${Number(value || 0).toLocaleString()}`;

function DeltaChip({ delta, format }) {
  if (!delta) return null;
  const positive = delta > 0;
  return (
    <span
      className={`ml-2 text-[11px] ${positive ? 'text-[#171A1D]' : 'text-[#527F19]'}`}
      style={{ fontFamily: 'IBM Plex Mono, monospace' }}
    >
      {positive ? '+' : '−'}{format(Math.abs(delta))}
    </span>
  );
}

/**
 * Side-by-side scenario comparison. Every column comes from an authoritative
 * server-side recalculation of that scenario's inputs.
 */
export function ScenarioComparison({ comparison, preferredIndex = null }) {
  const { t } = useTranslation();
  const scenarios = comparison?.scenarios || [];
  if (scenarios.length === 0) return null;

  const baseline = scenarios[0];
  const fmt = (scenario) => money(scenario.config);

  const rows = [
    {
      id: 'totalCost',
      label: t('scenarios.comparison.totalCost'),
      strong: true,
      render: (s) => (
        <>
          {fmt(s)(s.results.totalCost)}
          {s !== baseline && (
            <DeltaChip delta={s.results.totalCost - baseline.results.totalCost} format={fmt(s)} />
          )}
        </>
      ),
    },
    {
      id: 'planningRange',
      label: t('scenarios.comparison.planningRange'),
      render: (s) => `${fmt(s)(s.results.costRange.low)} – ${fmt(s)(s.results.costRange.high)}`,
    },
    {
      id: 'effort',
      label: t('scenarios.comparison.effort'),
      render: (s) => `${s.results.totalEffort} ${t('results.personDays')}`,
    },
    { id: 'internalDays', label: t('scenarios.comparison.internalDays'), render: (s) => s.results.internalDays },
    { id: 'consultantDays', label: t('scenarios.comparison.consultantDays'), render: (s) => s.results.consultantDays },
    {
      id: 'duration',
      label: t('scenarios.comparison.duration'),
      render: (s) => `${s.results.totalDurationWeeks} ${t('estimates.weeks')}`,
    },
    { id: 'auditDays', label: t('scenarios.comparison.auditDays'), render: (s) => s.results.auditDays },
    {
      id: 'confidence',
      label: t('scenarios.comparison.confidence'),
      render: (s) => t(`trust.${s.results.confidence?.level || 'moderate'}`),
    },
  ];

  const tcoRows = scenarios.every(s => s.tco) ? [
    { id: 'year1', label: t('scenarios.comparison.year1'), render: (s) => fmt(s)(s.tco.year1Total) },
    { id: 'recurring', label: t('scenarios.comparison.recurring'), render: (s) => fmt(s)(s.tco.recurringTotal) },
    {
      id: 'threeYearTotal',
      label: t('scenarios.comparison.threeYearTotal'),
      strong: true,
      render: (s) => (
        <>
          {fmt(s)(s.tco.total)}
          {s !== baseline && <DeltaChip delta={s.tco.total - baseline.tco.total} format={fmt(s)} />}
        </>
      ),
    },
  ] : [];

  const Header = ({ prefix }) => (
    <thead>
      <tr className="border-b border-[#DDE1E5]">
        <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.12em] text-[#66707A] font-medium"
            style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
          {t('scenarios.comparison.metric')}
        </th>
        {scenarios.map((scenario, index) => (
          <th scope="col" key={scenario.name + index} className="text-right px-4 py-3">
            <div className="text-sm font-medium text-[#171A1D] break-words" data-testid={`${prefix}-col-${index}`}>
              {scenario.name}
            </div>
            <div className="mt-0.5 text-[10px] uppercase tracking-[0.08em] text-[#8A929B]"
                 style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
              {index === 0 ? t('scenarios.baseline') : t('scenarios.alternative')}
            </div>
            {preferredIndex === index && (
              <div
                className="mt-1 inline-flex items-center border border-[#527F19]/35 bg-[#F1F8E8] px-1.5 py-0.5 rounded-sm text-[10px] font-medium uppercase tracking-[0.08em] text-[#527F19]"
                style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                data-testid={`${prefix}-selected-${index}`}
              >
                {t('scenarios.selectedScenario')}
              </div>
            )}
          </th>
        ))}
      </tr>
    </thead>
  );

  const Body = ({ items }) => (
    <tbody>
      {items.map(row => (
        <tr key={row.id} className="border-b border-[#DDE1E5] last:border-0" data-testid={`comparison-row-${row.id}`}>
          <td className={`px-4 py-2.5 text-sm ${row.strong ? 'font-medium text-[#171A1D]' : 'text-[#3A4249]'}`}>
            {row.label}
          </td>
          {scenarios.map((scenario, index) => (
            <td
              key={scenario.name + index}
              className={`px-4 py-2.5 text-right text-sm ${row.strong ? 'font-semibold text-[#171A1D]' : 'text-[#171A1D]'}`}
              data-column-label={scenario.name}
              style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            >
              {row.render(scenario)}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );

  return (
    <div className="space-y-5" data-testid="scenario-comparison">
      <div className="border border-[#DDE1E5] rounded-lg bg-white overflow-hidden">
        <div className="px-5 py-3.5 border-b border-[#DDE1E5]">
          <h2 className="text-lg font-medium tracking-tight text-[#171A1D]">{t('scenarios.comparison.title')}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full comparison-table">
            <Header prefix="comparison" />
            <Body items={rows} />
          </table>
        </div>
      </div>

      {tcoRows.length > 0 && (
        <div className="border border-[#DDE1E5] rounded-lg bg-white overflow-hidden" data-testid="scenario-comparison-tco">
          <div className="px-5 py-3.5 border-b border-[#DDE1E5]">
            <h2 className="text-lg font-medium tracking-tight text-[#171A1D]">{t('scenarios.comparison.tcoTitle')}</h2>
            <p className="mt-0.5 text-xs text-[#66707A]">{t('tco.note')}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full comparison-table">
              <Header prefix="comparison-tco" />
              <Body items={tcoRows} />
            </table>
          </div>
          <p className="px-5 py-3 border-t border-[#DDE1E5] text-xs text-[#8A929B]">
            {t('tco.recertExcluded')}
          </p>
        </div>
      )}

      <div className="border border-[#DDE1E5] rounded-lg bg-white overflow-hidden" data-testid="scenario-differences">
        <div className="px-5 py-3.5 border-b border-[#DDE1E5]">
          <h2 className="text-lg font-medium tracking-tight text-[#171A1D]">
            {t('scenarios.comparison.differencesTitle')}
          </h2>
          <p className="mt-0.5 text-xs text-[#66707A]">{t('scenarios.comparison.differencesIntro')}</p>
        </div>
        {(comparison.differences || []).length === 0 ? (
          <p className="px-5 py-4 text-sm text-[#66707A]" data-testid="scenario-differences-empty">
            {t('scenarios.comparison.differencesEmpty')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full comparison-table">
              <Header prefix="comparison-differences" />
              <tbody>
                {comparison.differences.map(diff => (
                  <tr key={diff.id} className="border-b border-[#DDE1E5] last:border-0"
                      data-testid={`difference-row-${diff.id}`}>
                    <td className="px-4 py-2.5 text-sm text-[#3A4249]">
                      {t(`assumptions.labels.${diff.id}`)}
                    </td>
                    {diff.values.map((entry, index) => (
                      <td key={`${diff.id}-${index}`} className="px-4 py-2.5 text-right" data-column-label={scenarios[index]?.name}>
                        <span className="text-sm text-[#171A1D]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                          {formatAssumptionValue(
                            { id: diff.id, value: entry.value },
                            { t, formatCost: money(scenarios[index]?.config) },
                          )}
                        </span>
                        {entry.provenance && (
                          <span className="block mt-1">
                            <ProvenanceBadge provenance={entry.provenance} />
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
